import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import 'firebase/messaging';
import { SharedService } from './shared.service';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Store } from '@ngxs/store';
import { Utility } from '../core/common';

const tokenKey = 'messaging/token';

type BroadcastMessage = { id?: string, type: string; payload?: any; };

@Injectable({
  providedIn: 'root'
})
export class PushService implements OnDestroy {
  static readonly pushNotificationMessageType = 'push-notification';

  private broadcastChannel: BroadcastChannel;
  private hubConnection: HubConnection;

  private unsubscribe$ = new Subject<void>();
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  constructor(
    private http: HttpClient, private sharedService: SharedService, private angularFireMessaging: AngularFireMessaging, private store: Store) {
    if (!this.sharedService.isBrowser) return;

    if (localStorage.getItem(tokenKey))
      this.requestPermission();
    else setTimeout(async () => {
      await this.checkNotificationStatus();
    });

    this.configureBroadcastChannels();
    this.configureAngularFire();
  }

  private messageBuffer: string[] = [];
  private readonly bufferSize = 100;
  private buffer(id: string) {
    if (!id) return;
    this.messageBuffer.push(id);
    if (this.messageBuffer.length > this.bufferSize) this.messageBuffer.shift();
  }
  private message$ = new Subject<BroadcastMessage>();
  onMessage$<T>(...types: string[]) {
    return this.message$.asObservable().pipe(
      filter(message => types.includes(message.type)),
      map(message => message.payload as T)
    );
  }

  private token: string;
  async configureHub() {
    var token = this.store.selectSnapshot(state => state.auth?.token);
    if (token === this.token && this.hubConnection?.state != HubConnectionState.Disconnected)
      return;
    this.token = token;

    try { await this.hubConnection?.stop(); }
    catch (err) { console.error('Error stopping hub connection:', err); }

    if (token) {
      this.hubConnection = new HubConnectionBuilder()
        .withAutomaticReconnect()
        .withUrl(`${environment.app.hubUrl}?auth=${token}`, { skipNegotiation: true, transport: HttpTransportType.WebSockets })
        .build();

      this.hubConnection.start().then(() => {
        this.hubConnection.on('message', event => {
          // console.log('message', event);
          if (event.data)
            this.postBroadcastMessage(event.data);
        });
        this.hubConnection.on('ping', event => {
          console.log('ping', event);
        });
      }).catch((err) => {
        console.error('Error starting hub connection:', err);
        setTimeout(() => this.configureHub(), 10000);
      });
    }
  }

  private configureAngularFire() {
    this.angularFireMessaging.messages.subscribe(async (payload) => {
      // console.log('onForgroundMessage', payload);
      try {
        const message = JSON.parse(payload.data['gcm.notification.data'] || payload.data.data);
        if (message.type == PushService.pushNotificationMessageType && payload.notification != null)
          new Notification(payload.notification.title,
            Utility.assign(payload.notification, { tag: (<any>payload).messageId }, true));
        this.postBroadcastMessage(message);
      }
      catch (err) { console.error('onForgroundMessage Error', err); }
    });
  }

  async cancelNotification(tag: string) {
    const firebaseRegistration = (await navigator.serviceWorker.getRegistrations())
      .find(_ => _.active?.scriptURL?.includes('firebase-messaging-sw.js'));
    firebaseRegistration?.getNotifications({ tag })
      .then(notifications => notifications.forEach(notification => notification.close()));
  }

  private configureBroadcastChannels() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('kp_broadcast');
      this.broadcastChannel.onmessage = (event: MessageEvent) => {
        const message = <BroadcastMessage>event.data;
        if (!message || this.messageBuffer.includes(message.id)) return;
        this.buffer(message.id);
        this.message$.next(message);
      };
    }
  }

  postBroadcastMessage(message: BroadcastMessage) {
    if (!this.broadcastChannel) return;
    this.message$.next(message);
    this.broadcastChannel.postMessage(message);
  }

  status$ = new BehaviorSubject('messaging/permission-unknown');
  isSupported: boolean;

  private async removeToken(token?: string) {
    if (token)
      await this.http.post<any>(environment.app.apiUrl + 'notification/delete-push', { token }).toPromise();
    localStorage.removeItem(tokenKey);
  }

  async registerPush() { await this.saveToken(); }

  private async saveToken(token?: string) {
    var saved = localStorage.getItem(tokenKey);
    if (!token && !saved) return;
    if (!token) token = saved;
    if (token !== saved)
      await this.removeToken(saved);

    if (token) {
      await this.http.post<any>(environment.app.apiUrl + 'notification/register-push', { token }).toPromise();
      localStorage.setItem(tokenKey, token);
    }
  }

  async requestPermission() {
    if (this.isSupported == false) return;
    return new Promise<boolean>(resolve => {
      this.angularFireMessaging.tokenChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(async token => {
          var saved = localStorage.getItem(tokenKey);
          if (token && token === saved) {
            this.status$.next('messaging/permission-allowed');
            resolve(true);
            return;
          }

          if (!token && this.status$.value == 'messaging/permission-allowed') {
            this.removeToken();
            this.status$.next(null);
            resolve(false);
            return;
          }

          this.status$.next('messaging/permission-checking');
          var _token = await this.angularFireMessaging.getToken.toPromise();
          if (_token) {
            await this.saveToken(token);
            this.status$.next('messaging/permission-allowed');
            resolve(true);
            return;
          }

          this.angularFireMessaging.requestPermission
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
              async (status) => resolve(await this.checkNotificationStatus(status)),
              async error => {
                this.status$.next(error.code);
                switch (error.code) {
                  case 'messaging/permission-blocked':
                    await this.removeToken(saved);
                    break;
                  case 'messaging/permission-default':
                    this.removeToken();
                    break;
                }
                resolve(false);
              }
            );
        }, error => {
          console.warn(error);
          resolve(false);
        });
    });
  }

  private _canLocate: boolean;
  private async checkNotificationStatus(status?: string) {
    if (!status) {
      if ("Notification" in window)
        status = Notification.permission;
      else status = "unsupported";
    }
    switch (status) {
      case 'granted':
        var _token = await this.angularFireMessaging.getToken.toPromise();
        await this.saveToken(_token);
        this.status$.next('messaging/permission-allowed');
        return true;
      case 'denied':
        this.status$.next('messaging/permission-blocked');
        return false;
      case 'unsupported':
        this.status$.next('messaging/unsupported-browser');
        return false;
      default:
        this.status$.next('messaging/permission-default');
        return false;
    }
  }

  get canLocate() {
    return false && //Google 403
      this._canLocate != false && !!navigator.geolocation;
  }
  async requestLocation(enableHighAccuracy = true, timeout = Infinity, maximumAge = Infinity) {
    return new Promise<any>(resolve => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async position => {
          var location = await this.http.post<any>(environment.app.apiUrl + 'notification/set-location', {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: Math.floor(position.coords.accuracy / 1000),
          }).toPromise();
          resolve(location);
        },
        err => {
          this._canLocate = false;
          console.warn(err);
          resolve(null);
        },
        { enableHighAccuracy, timeout, maximumAge });
    });
  }

  adminSend(notification: any): any {
    return this.http.post<any>(environment.app.apiUrl + 'notification/admin-send', notification).toPromise();
  }
  testPush(notification: any, token?: string): any {
    return this.http.post<any>(environment.app.apiUrl + `notification/push/test`, notification, {
      params: Utility.removeEmpty({ token })
    }).toPromise();
  }

  setIsRead(id: any = 'all', isRead: boolean = true) {
    return this.http.get(environment.app.apiUrl + `notification/message/read/${id}${(id == 'all' ? '' : `/${isRead}`)}`).toPromise();
  }

  read(...id: string[]) {
    return this.http.post(environment.app.apiUrl + `notification/message/read`, id).toPromise();
  }

  getMessages(from: number = 0, limit: number = 100) {
    return this.http.get<any[]>(environment.app.apiUrl + `notification/messages/${from}/${limit}`).toPromise();
  }

  search(criteria: any) {
    return this.http.post<{ List: any[], Total: number }>(environment.app.apiUrl + `notification/search`, criteria).toPromise();
  }

  checkStatus(id: string) {
    return this.http.get<any>(environment.app.apiUrl + `notification/check-status/${id}`).toPromise();
  }

  kavenegarStatus(status: number): string {
    switch (status) {
      case 1: return 'در صف ارسال';
      case 2: return 'ارسال در آینده';
      case 4:
      case 5: return 'ارسال به مخابرات';
      case 6: return 'خطا در ارسال';
      case 10: return 'رسیده به گوشی';
      case 11: return 'خط دردسترس نیست';
      case 13:
      case 14: return 'بلاک شده';
      default:
        return 'نامشخص';
    }
  }
}
